import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  model,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, Observable, of, startWith, switchMap } from 'rxjs';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import {
  NgHeaderTemplateDirective,
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { ValidationFormComponent } from '../validation-form/validation-form.component';
import { SelectOptionsInterface, TranslocoFunction } from '../../types';
import { uiKitScope } from '../../constants/ui-constant';
import { provideUiKitScope } from '../../providers';
import { KeeniconComponent } from '../../partials';

@Component({
  selector: 'ui-form-multi-select',
  imports: [
    NgSelectComponent,
    TranslocoPipe,
    ValidationFormComponent,
    ReactiveFormsModule,
    SlicePipe,
    NgMultiLabelTemplateDirective,
    FormsModule,
    NgOptionTemplateDirective,
    NgHeaderTemplateDirective,
    NgClass,
    NgTemplateOutlet,
    TranslocoDirective,
    KeeniconComponent,
  ],
  templateUrl: './form-multi-select.component.html',
  styleUrl: './form-multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideUiKitScope()],
  host: { '[attr.data-cy]': 'dataCy() || null' },
})
export class FormMultiSelectComponent {
  dataCy = input<string>('');
  control = input<AbstractControl>();
  placeholder = input<string>('');
  skipValidation = input<boolean>(false);
  searchable = input<boolean>(false);
  class = input<string>('');
  isTransloco = input<boolean>(false);
  virtualScroll = input<boolean>(false);
  hiddenSelected = input<boolean>(false);
  showSelectAll = input<boolean>(true);
  dataSelect = input<SelectOptionsInterface[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value = model<any>([]);
  customOptionTemplate = contentChild<TemplateRef<unknown>>(TemplateRef);
  description = input<string>('');
  lockedMessage = input<string>('');
  appendTo = input<string>('');
  t = input<TranslocoFunction>();
  closeSelect = output<void>();
  valueChange = output<string[]>();
  searchTerm = signal('');

  private readonly controlValue = toSignal(
    toObservable(this.control).pipe(
      switchMap((ctrl) =>
        ctrl
          ? (ctrl.valueChanges.pipe(startWith(ctrl.value)) as Observable<
              (string | number)[]
            >)
          : of<(string | number)[]>([]),
      ),
      map((value) => value ?? []),
    ),
    { initialValue: [] as (string | number)[] },
  );

  filteredItems = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const data = this.dataSelect() ?? [];
    const selectedIds: (string | number)[] = this.control()
      ? this.controlValue()
      : this.value() || [];
    const selectedSet = new Set(selectedIds);

    const filtered = term
      ? data.filter(
          (item) =>
            item.name.toLowerCase().includes(term) || selectedSet.has(item.id),
        )
      : data;

    const selected: SelectOptionsInterface[] = [];
    const unselected: SelectOptionsInterface[] = [];

    for (const item of filtered) {
      if (selectedSet.has(item.id)) {
        selected.push(item);
      } else {
        unselected.push(item);
      }
    }

    return [...selected, ...unselected];
  });

  get isAllSelected(): boolean {
    const selectableIds = (this.dataSelect() ?? []).map((item) => item.id);
    if (selectableIds.length === 0) return false;
    const currentSelection = this.formControl?.value || this.value() || [];
    return selectableIds.every((id) => currentSelection.includes(id));
  }

  get formControl(): FormControl {
    return this.control() as FormControl;
  }

  truncate(value: string | null | undefined): string {
    if (!value) return '';
    return value.length > 6 ? value.substring(0, 6) + '...' : value;
  }

  selectAll() {
    if (!this.dataSelect()) return;
    const selectableIds = this.dataSelect().map((item) => item.id);
    const currentSelection = this.formControl.value || [];
    const allSelected = selectableIds.every((id) =>
      currentSelection.includes(id),
    );

    if (allSelected) {
      this.formControl.setValue([]);
    } else {
      const updatedSelection = Array.from(
        new Set([...currentSelection, ...selectableIds]),
      );
      this.formControl.setValue(updatedSelection);
    }
  }

  selectModelAll() {
    if (!this.dataSelect()) return;
    const selectableIds = this.dataSelect().map((item) => item.id);
    const currentSelection = this.value() || [];
    const allSelected = selectableIds.every((id) =>
      currentSelection.includes(id),
    );

    if (allSelected) {
      this.value.set([]);
      this.valueChange.emit([]);
    } else {
      this.value.set(selectableIds);
      this.valueChange.emit(selectableIds);
    }
  }

  onSelectChange(selectedItems: SelectOptionsInterface[] | null) {
    const items = selectedItems || [];

    let selectedIds: string[];
    if (!Array.isArray(items) || items.length === 0) {
      selectedIds = [];
    } else if (
      typeof items[0] === 'object' &&
      items[0] !== null &&
      'id' in items[0]
    ) {
      selectedIds = items.map((item) => item.id);
    } else {
      selectedIds = items.map((item) => String(item));
    }

    this.valueChange.emit(selectedIds);
  }

  removeItem(itemId: number) {
    const currentSelection = this.formControl.value || [];
    this.formControl.setValue(
      currentSelection.filter((id: number) => id !== itemId),
    );
    this.onSelectChange(this.formControl.value);
  }

  removeModelItem(itemId: string) {
    const currentSelection = this.value() || [];
    const newSelection = currentSelection.filter((id: string) => id !== itemId);
    this.value.set(newSelection);
    this.valueChange.emit(newSelection);
  }

  onModelChange(selectedIds: string[]) {
    this.value.set(selectedIds);
    this.valueChange.emit(selectedIds);
  }

  onSearchInput(event: Event) {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  clearSearch() {
    this.searchTerm.set('');
  }

  protected readonly uiKitScope = uiKitScope;
}
